import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';

/**
 * DeleteMultipleDialog component - A reusable confirmation dialog for deleting multiple items
 * 
 * @component
 */
const DeleteMultipleDialog = ({ open, onClose, onConfirm, items }) => {
  // Determine if we're deleting a mix of containers and items
  const hasMixedTypes = items.some(item => item.attributes?.type === 'container') && 
                       items.some(item => item.attributes?.type !== 'container');
  
  // Determine the appropriate item type label
  const getTypeLabel = () => {
    if (hasMixedTypes) return 'items';
    if (items.length === 0) return 'items';
    return items[0]?.attributes?.type === 'container' ? 'containers' : 'requirements';
  };

  // Handle keyboard shortcuts within the dialog
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onConfirm();
      event.preventDefault();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-multiple-dialog-title"
      aria-describedby="delete-multiple-dialog-description"
      maxWidth="sm"
      fullWidth
      onKeyDown={handleKeyDown}
    >
      <DialogTitle id="delete-multiple-dialog-title">
        Delete Multiple {getTypeLabel()}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-multiple-dialog-description">
          Are you sure you want to delete these {items.length} {getTypeLabel()}? This action cannot be undone.
        </DialogContentText>
        
        {items.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Selected items:
            </Typography>
            <List dense sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
              {items.map((item) => (
                <ListItem key={item.id}>
                  <ListItemText 
                    primary={item.title || 'Unnamed item'} 
                    secondary={`ID: ${item.id} ${item.attributes?.type === 'container' ? '(Container)' : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteMultipleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired
};

export default DeleteMultipleDialog;
